import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';

/**
 * Chip — a filter, a tag, or a small piece of state.
 *
 * §11, the colour rule: `ok` is green ONLY where something is verified, `warn`
 * is amber ONLY where time or stock is running out, `bad` is red ONLY where
 * something is blocked. A chip that says "Expiry ≥ 6mo" is green because it is
 * an assertion about verification; a chip that said "Free delivery" in green
 * would be a lie in the system's own vocabulary.
 */

type Tone = 'default' | 'selected' | 'soft' | 'ok' | 'warn' | 'bad';

const tones: Record<Tone, string> = {
  default: 'bg-paper border-line text-muted',
  selected: 'bg-blue border-blue text-white',
  soft: 'bg-blue-tint border-blue-tint text-blue',
  ok: 'bg-verified-tint border-verified-tint text-verified',
  warn: 'bg-caution-tint border-caution-tint text-caution',
  bad: 'bg-danger-tint border-danger-tint text-danger',
};

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: Tone;
  icon?: IconName;
  iconFilled?: boolean;
  /** Renders a <span>. Use for a chip that only reports, never acts. */
  readOnly?: boolean;
  children: ReactNode;
}

export function Chip({
  tone = 'default',
  icon,
  iconFilled = false,
  readOnly = false,
  className,
  children,
  ...props
}: ChipProps) {
  const classes = cn(
    'inline-flex items-center gap-[5px] whitespace-nowrap rounded-pill border',
    'px-[11px] py-[5px] text-[11.5px] font-medium',
    tones[tone],
    !readOnly && 'cursor-pointer transition-colors',
    className,
  );

  const content = (
    <>
      {icon ? <Icon name={icon} size={12} filled={iconFilled} strokeWidth={2} /> : null}
      {children}
    </>
  );

  if (readOnly) {
    return <span className={classes}>{content}</span>;
  }

  return (
    <button type="button" aria-pressed={tone === 'selected'} className={classes} {...props}>
      {content}
    </button>
  );
}

/**
 * A horizontally scrollable row of chips.
 *
 * The scrollbar is hidden on purpose: on a 393px screen a visible scrollbar
 * eats into a 44px touch target, and the overflow is already signalled by a
 * chip being cut off at the edge.
 */
export function ChipRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('scrollbar-none flex gap-[7px] overflow-x-auto pb-0.5', className)}>
      {children}
    </div>
  );
}
